/**
 * Section frame and anchor pills for the deal AgentDart tab.
 *
 * Layout only: every card inside keeps its own data and behaviour.
 */
import * as React from "react";

export const AGENTDART_SECTIONS = [
  { key: "setup", id: "agentdart-setup", label: "Setup" },
  { key: "goal", id: "agentdart-goal", label: "Goal" },
  { key: "stats", id: "agentdart-stats", label: "Stats" },
  { key: "needs", id: "agentdart-needs-you", label: "Needs you" },
  { key: "activity", id: "agentdart-activity", label: "Activity" },
] as const;

export type AgentDartTabKey = (typeof AGENTDART_SECTIONS)[number]["key"];

export function isAgentDartTabKey(v: unknown): v is AgentDartTabKey {
  return AGENTDART_SECTIONS.some((s) => s.key === v);
}

/**
 * Tab switcher for the AgentDart tab. Only the selected section is rendered,
 * so the row is a control, not an anchor list.
 */
export function AgentDartTabs({
  active,
  onSelect,
  counts,
}: {
  active: AgentDartTabKey;
  onSelect: (key: AgentDartTabKey) => void;
  counts?: Partial<Record<AgentDartTabKey, number>>;
}) {
  return (
    <nav aria-label="Sections">
      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
        {AGENTDART_SECTIONS.map((s) => {
          const selected = s.key === active;
          const count = counts?.[s.key] ?? 0;
          return (
            <button
              key={s.key}
              type="button"
              aria-current={selected ? "true" : undefined}
              onClick={() => onSelect(s.key)}
              className={
                selected
                  ? "rounded-full border border-brand-ink bg-brand-ink px-3 py-1 text-[13px] text-brand-paper"
                  : "rounded-full border border-brand-line bg-brand-paper px-3 py-1 text-[13px] text-brand-ink hover:border-brand-accent"
              }
            >
              {s.label}
              {count > 0 ? (
                <span
                  className={
                    selected
                      ? "ml-1.5 text-[12px] text-brand-paper/80"
                      : "ml-1.5 text-[12px] text-brand-muted"
                  }
                >
                  {count}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </nav>
  );
}

export function AgentDartSection({
  id,
  title,
  children,
}: {
  id: string;
  /** Left out when the card inside already carries the heading. */
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-20 space-y-3">
      {title ? (
        <h2 className="font-serif text-xl text-brand-ink">{title}</h2>
      ) : null}
      {children}
    </section>
  );
}
